import { Injectable, inject } from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { Identifier } from '../shared/interfaces/identifier.model';
import { ResearcherLink } from '../shared/interfaces/researcher-link.model';
import { ProfileService } from './profile.service';
import { ResearcherProfile } from '../shared/interfaces/researcher-profile.interface';
import { ResearcherOverview } from '../shared/interfaces/researcher-overview.interface';
import { Identifiers } from '../shared/configurations/identifiers.constant';
import { ResearcherPhone } from '../shared/interfaces/researcher-phone.model';
import { SortUtils } from '../shared/utils/sort.utils';
import { PersonOrganizationAffiliationDataWrapper } from '../shared/interfaces/person-organization-affiliation-data-wrapper.model';
import { ResearcherMetrics } from '../shared/interfaces/researcher-metrics.interface';

@Injectable({
    providedIn: 'root',
})
export class ProfileDataService {
    private formBuilder = inject(UntypedFormBuilder);
    profileService = inject(ProfileService);

    profileForm: UntypedFormGroup;
    researcherHighlights = [];
    researcherGrantsHighlights = [];
    ordicRegex = 'http(s)?:\\/\\/(www\\.)?orcid.org\\/';
    scopusIdRegex =
        'http(s)?:\\/\\/www\\.scopus\\.com\\/authid\\/detail\\.uri\\?authorId=';
    isniRegex = 'http(s)?:\\/\\/www\\.isni\\.org\\/isni\\/';
    researcherIdRegex = 'http(s)?:\\/\\/publons\\.com\\/researcher\\/';
    viafRegex = 'http(s)?:\\/\\/viaf\\.org\\/viaf\\/';
    wikidataRegex = 'http(s)?:\\/\\/www\\.wikidata\\.org\\/wiki\\/';
    googleScholarIDRegex = 'http(s)?:\\/\\/scholar\\.google\\.com\\/citations\\?user=';
    arxivRegex = 'http(s)?:\\/\\/arxiv\\.org\\/a\\/';
    pivotRegex = 'http(s)?:\\/\\/pivot\\.proquest\\.com\\/profiles\\/';

    constructor() {
        this.createForm();
    }

    createForm() {
        this.profileForm = this.formBuilder.group({
            topics: this.formBuilder.array([]),
            displayName: '',
            displayTitle: '',
            firstName: '',
            lastName: '',
            userTitle: '',
            additionalName: '',
            affiliation: '',
            affiliationAncestors: this.formBuilder.array([]),
            position: '',
            expertise: this.formBuilder.array([]),
            nameVariants: this.formBuilder.array([]),
            associations: this.formBuilder.array([]),
            emails: this.formBuilder.array([]),
            links: this.formBuilder.array([]),
            identifiers: this.formBuilder.array([]),
            phones: this.formBuilder.array([]),
            addresses: this.formBuilder.array([]),
            orcid: this.formBuilder.array([]),
            insi: this.formBuilder.array([]),
            arxiv: this.formBuilder.array([]),
            era: this.formBuilder.array([]),
            pivot: this.formBuilder.array([]),
            pubmed: this.formBuilder.array([]),
            researcherID: this.formBuilder.array([]),
            wikidata: this.formBuilder.array([]),
            viaf: this.formBuilder.array([]),
            googleScholarID: this.formBuilder.array([]),
            scopus: this.formBuilder.array([]),
            lcnaf: this.formBuilder.array([]),
            curAffiliations: this.formBuilder.array([]),
            prevAffiliations: this.formBuilder.array([]),
            researcherImageUrl: '',
            educations: this.formBuilder.array([]),
            honors: [],
            metrics: {},
            engagements: [],
        });
    }

    get topics(): UntypedFormArray {
        return this.profileForm.get('topics') as UntypedFormArray;
    }

    get phones(): UntypedFormArray {
        return this.profileForm.get('phones') as UntypedFormArray;
    }

    get addresses(): UntypedFormArray {
        return this.profileForm.get('addresses') as UntypedFormArray;
    }

    get displayName(): UntypedFormControl {
        return this.profileForm.get('displayName') as UntypedFormControl;
    }

    get firstName(): UntypedFormControl {
        return this.profileForm.get('firstName') as UntypedFormControl;
    }

    get lastName(): UntypedFormControl {
        return this.profileForm.get('lastName') as UntypedFormControl;
    }

    get userTitle(): UntypedFormControl {
        return this.profileForm.get('userTitle') as UntypedFormControl;
    }

    get additionalName(): UntypedFormControl {
        return this.profileForm.get('additionalName') as UntypedFormControl;
    }

    get affiliation(): UntypedFormControl {
        return this.profileForm.get('affiliation') as UntypedFormControl;
    }

    get affiliationAncestors(): UntypedFormArray {
        return this.profileForm.get('affiliationAncestors') as UntypedFormArray;
    }

    get displayTitle(): UntypedFormControl {
        return this.profileForm.get('displayTitle') as UntypedFormControl;
    }

    get position(): UntypedFormControl {
        return this.profileForm.get('position') as UntypedFormControl;
    }

    get expertise(): UntypedFormArray {
        return this.profileForm.get('expertise') as UntypedFormArray;
    }

    get nameVariants(): UntypedFormArray {
        return this.profileForm.get('nameVariants') as UntypedFormArray;
    }

    get researcherImageUrl(): UntypedFormControl {
        return this.profileForm.get('researcherImageUrl') as UntypedFormControl;
    }

    get associations(): UntypedFormArray {
        return this.profileForm.get('associations') as UntypedFormArray;
    }

    get emails() {
        return this.profileForm.get('emails') as UntypedFormArray;
    }

    get links(): UntypedFormArray {
        return this.profileForm.get('links') as UntypedFormArray;
    }

    get curAffiliations(): UntypedFormArray {
        return this.profileForm.get('curAffiliations') as UntypedFormArray;
    }

    get prevAffiliations(): UntypedFormArray {
        return this.profileForm.get('prevAffiliations') as UntypedFormArray;
    }

    get identifiers(): UntypedFormArray {
        return this.profileForm.get('identifiers') as UntypedFormArray;
    }

    get orcid(): UntypedFormArray {
        return this.profileForm.get('orcid') as UntypedFormArray;
    }

    get insi(): UntypedFormArray {
        return this.profileForm.get('insi') as UntypedFormArray;
    }

    get arxiv(): UntypedFormArray {
        return this.profileForm.get('arxiv') as UntypedFormArray;
    }

    get era(): UntypedFormArray {
        return this.profileForm.get('era') as UntypedFormArray;
    }

    get pivot(): UntypedFormArray {
        return this.profileForm.get('pivot') as UntypedFormArray;
    }

    get pubmed(): UntypedFormArray {
        return this.profileForm.get('pubmed') as UntypedFormArray;
    }

    get researcherID(): UntypedFormArray {
        return this.profileForm.get('researcherID') as UntypedFormArray;
    }

    get scopus(): UntypedFormArray {
        return this.profileForm.get('scopus') as UntypedFormArray;
    }

    get wikidata(): UntypedFormArray {
        return this.profileForm.get('wikidata') as UntypedFormArray;
    }

    get viaf(): UntypedFormArray {
        return this.profileForm.get('viaf') as UntypedFormArray;
    }

    get googleScholarID(): UntypedFormArray {
        return this.profileForm.get('googleScholarID') as UntypedFormArray;
    }

    get lcnaf(): UntypedFormArray {
        return this.profileForm.get('lcnaf') as UntypedFormArray;
    }

    get educations(): UntypedFormArray {
        return this.profileForm.get('educations') as UntypedFormArray;
    }

    get honors() {
        return this.profileForm.get('honors');
    }

    get metrics() {
        return this.profileForm.get('metrics');
    }

    get engagements() {
        return this.profileForm.get('engagements');
    }

    updateDisplayName(displayName) {
        this.profileForm.patchValue({ displayName: displayName });
    }

    updateFirstName(firstName) {
        this.profileForm.patchValue({ firstName: firstName });
    }

    updateLastName(lastName) {
        this.profileForm.patchValue({ lastName: lastName });
    }

    updateResearcherImageUrl(researcherImageUrl) {
        this.profileForm.patchValue({ researcherImageUrl: researcherImageUrl });
    }

    updateUserTitle(userTitle) {
        this.profileForm.patchValue({ userTitle: userTitle });
    }

    updateAdditionalName(additionalName) {
        this.profileForm.patchValue({ additionalName: additionalName });
    }

    updateAffiliation(affiliation) {
        this.profileForm.patchValue({ affiliation: affiliation });
    }

    updateAffiliationAncestors(affiliationAncestors) {
        this.profileForm.setControl(
            'affiliationAncestors',
            this.formBuilder.array(affiliationAncestors)
        );
    }

    updateDisplayTitle(displayTitle: string) {
        this.profileForm.patchValue({ displayTitle: displayTitle });
    }

    updatePosition(position) {
        this.profileForm.patchValue({ position: position });
    }

    addEmail(email) {
        this.emails.push(this.formBuilder.control(email));
    }

    addPhone(phone: ResearcherPhone) {
        const group = this.formBuilder.group({
            phone: phone.phone,
            type: phone.type[0],
        });
        this.phones.push(group);
    }

    addOrcid(orcid) {
        this.orcid.push(this.formBuilder.control(orcid));
    }

    addInsi(insi) {
        this.insi.push(this.formBuilder.control(insi));
    }

    addArxiv(arxiv) {
        this.arxiv.push(this.formBuilder.control(arxiv));
    }

    addEra(era) {
        this.era.push(this.formBuilder.control(era));
    }

    addPivot(pivot) {
        this.pivot.push(this.formBuilder.control(pivot));
    }

    addPubmed(pubmed) {
        this.pubmed.push(this.formBuilder.control(pubmed));
    }

    addResearcherID(researcherID) {
        this.researcherID.push(this.formBuilder.control(researcherID));
    }

    addScopus(scopus) {
        this.scopus.push(this.formBuilder.control(scopus));
    }

    addwWikidata(wikidata) {
        this.wikidata.push(this.formBuilder.control(wikidata));
    }

    addViaf(viaf) {
        this.viaf.push(this.formBuilder.control(viaf));
    }

    addGoogleScholarID(googleScholarID) {
        this.googleScholarID.push(this.formBuilder.control(googleScholarID));
    }

    addLcnaf(lcnaf) {
        this.lcnaf.push(this.formBuilder.control(lcnaf));
    }

    addTopic(topic) {
        this.topics.push(this.formBuilder.control(topic));
    }

    addIdentifier(id) {
        this.identifiers.push(this.formBuilder.group(id));
    }

    addExpertise(expertise) {
        this.expertise.push(this.formBuilder.control(expertise));
    }

    addNameVariant(nameVariant) {
        this.nameVariants.push(this.formBuilder.control(nameVariant));
    }

    addAssociation(association) {
        this.associations.push(this.formBuilder.control(association));
    }

    addLink(link) {
        link.setIcon(link.url);
        this.links.push(this.formBuilder.group(link));
    }

    addAddress(address) {
        this.addresses.push(this.formBuilder.group(address));
    }

    addCurrAffiliation(affiliation: PersonOrganizationAffiliationDataWrapper) {
        this.curAffiliations.push(
            this.createFormGroupFromAffiliationObj(affiliation)
        );
    }

    addPrevAffiliation(affiliation: PersonOrganizationAffiliationDataWrapper) {
        this.prevAffiliations.push(
            this.createFormGroupFromAffiliationObj(affiliation)
        );
    }

    addEducation(education) {
        this.educations.push(this.formBuilder.control(education));
    }

    createFormGroupFromAffiliationObj(
        affiliation: PersonOrganizationAffiliationDataWrapper
    ) {
        const group = this.formBuilder.group({
            data: affiliation.data,
            category: affiliation.category,
            ancestors: this.formBuilder.array(affiliation.ancestors),
        });
        return group;
    }

    parseData(profileData: ResearcherProfile) {
        profileData.topics.forEach((topic) => {
            this.addTopic(topic);
        });
        this.updateDisplayName(profileData.displayName);
        this.profileService.nameChange$.next(profileData.displayName);
        let position = profileData.position;
        this.updateDisplayTitle(profileData.displayTitle);
        this.updateAdditionalName(profileData.additionalName);
        if (profileData.currentAffiliation) {
            this.updateAffiliation(profileData.currentAffiliation);
            this.updateAffiliationAncestors(
                profileData.currentAffiliationAncestors
            );
            if (profileData.currentAffiliation.position) {
                position = profileData.currentAffiliation.position;
            }
        }
        this.updatePosition(position);
    }

    parseDataForSEO(profileData: ResearcherProfile) {
        this.updateFirstName(
            profileData.firstName ? profileData.firstName : ''
        );
        this.updateLastName(profileData.lastName);
        this.updateUserTitle(
            profileData.userTitle ? profileData.userTitle : ''
        );
        this.updateResearcherImageUrl(profileData.researcherImageUrl);
        profileData.nameVariants.forEach((name) => {
            this.addNameVariant(name);
        });
        profileData.associations.forEach((association) => {
            this.addAssociation(association);
        });
    }

    parseOverview(overviewData: ResearcherOverview) {
        overviewData.areaOfInterestList.forEach((expertise) => {
            this.addExpertise(expertise);
        });
        overviewData.links.forEach((link) => {
            const title = link.title ? link.title : link.url;
            const newLink = new ResearcherLink(
                link.url,
                title,
                link.description
            );
            this.addLink(newLink);
        });
        overviewData.identifiers.forEach((identifier) => {
            this.updateExactIdentifier(identifier);
        });

        //the order of the identifiers are important
        this.orcid.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.ORCID, value);
            this.addIdentifier(identifier);
        });
        this.insi.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.INSI, value);
            this.addIdentifier(identifier);
        });
        this.pivot.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.PIVOT, value);
            this.addIdentifier(identifier);
        });
        this.scopus.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.SCOPUS, value);
            this.addIdentifier(identifier);
        });
        this.pubmed.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.PUBMED, value);
            this.addIdentifier(identifier);
        });
        this.lcnaf.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.LCNAF, value);
            this.addIdentifier(identifier);
        });
        this.arxiv.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.ARXIV, value);
            this.addIdentifier(identifier);
        });
        this.era.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.ERA, value);
            this.addIdentifier(identifier);
        });
        this.wikidata.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.WIKIDATA, value);
            this.addIdentifier(identifier);
        });
        this.viaf.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.VIAF, value);
            this.addIdentifier(identifier);
        });
        this.googleScholarID.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.GOOGLE_SCHOLAR_ID, value);
            this.addIdentifier(identifier);
        });
        this.researcherID.value.forEach((value) => {
            const identifier = new Identifier(Identifiers.RESEARCHER_ID, value);
            this.addIdentifier(identifier);
        });

        overviewData.addresses.forEach((address) => {
            this.addAddress(address);
        });

        overviewData.curAffiliations.sort(SortUtils.affiliationSortByOrder);
        overviewData.prevAffiliations.sort(SortUtils.affiliationSortByOrder);

        overviewData.curAffiliations.forEach((affiliation) => {
            this.addCurrAffiliation(affiliation);
        });

        overviewData.prevAffiliations.forEach((affiliation) => {
            this.addPrevAffiliation(affiliation);
        });

        overviewData.emails.forEach((email) => {
            this.addEmail(email);
        });

        overviewData.phones.forEach((phone) => {
            this.addPhone(phone);
        });

        overviewData.educations.forEach((education) => {
            this.addEducation(education);
        });
        this.profileForm.patchValue({ honors: overviewData.honors });
        this.profileForm.patchValue({engagements: overviewData.engagements});
    }
    parseMetrics(metrics: ResearcherMetrics) {
        this.profileForm.patchValue({ metrics });
    }

    //the same identifier can have several values
    updateExactIdentifier(identifier: Identifier) {
        const type = identifier.type;
        const value = identifier.value;
        if (type === Identifiers.ORCID) {
            const orcid = this.normalizeValue(value, this.ordicRegex);
            this.addOrcid(orcid);
        }
        if (type === Identifiers.INSI) {
            const insi = this.normalizeValue(value, this.isniRegex);
            this.addInsi(insi);
        }
        if (type === Identifiers.ARXIV) {
            const arxiv = this.normalizeValue(value, this.arxivRegex);
            this.addArxiv(arxiv);
        }
        if (type === Identifiers.ERA) {
            this.addEra(value);
        }
        if (type === Identifiers.PIVOT) {
            const pivot = this.normalizeValue(value, this.pivotRegex);
            this.addPivot(pivot);
        }
        if (type === Identifiers.PUBMED) {
            this.addPubmed(value);
        }
        if (type === Identifiers.RESEARCHER_ID) {
            const researcherId = this.normalizeValue(
                value,
                this.researcherIdRegex
            );
            this.addResearcherID(researcherId);
        }
        if (type === Identifiers.SCOPUS) {
            const scopus = this.normalizeValue(value, this.scopusIdRegex);
            this.addScopus(scopus);
        }
        if (type === Identifiers.WIKIDATA) {
            const wikidata = this.normalizeValue(value, this.wikidataRegex);
            this.addwWikidata(wikidata);
        }
        if (type === Identifiers.VIAF) {
            const viaf = this.normalizeValue(value, this.viafRegex);
            this.addViaf(viaf);
        }
        if (type === Identifiers.GOOGLE_SCHOLAR_ID) {
            const googleScholarID = this.normalizeValue(value, this.googleScholarIDRegex);
            this.addGoogleScholarID(googleScholarID);
        }
        if (type === Identifiers.LCNAF) {
            this.addLcnaf(value);
        }
    }

    normalizeValue(value, regex) {
        return value.replace(new RegExp(regex), '');
    }
}
